import { Toaster } from "react-hot-toast";
import { Logo } from "@calcom/ui";
import { useLocale } from "@calcom/lib/hooks/useLocale";
import { useEffect, useRef } from "react";

type OnboardingLayoutProps = {
  children: React.ReactNode;
  isEmbed?: boolean;
};

export function OnboardingLayout({ children, isEmbed = false }: OnboardingLayoutProps) {
  const { t } = useLocale();
  const iframeSizeRef = useIFrameSizeNotifier();

  if (isEmbed) {
    return (
      <>
        <div>
          <Toaster position="bottom-right" />
        </div>
        <div
          ref={iframeSizeRef}
          className="grid min-h-screen grid-cols-1 items-center justify-center gap-12 bg-[#fdfcfa] p-4 md:grid-cols-2">
          <div className="min-w-0 sm:flex">
            <div className="font-sans">
              <h2 className="mb-5 font-serif text-3xl font-bold leading-tight text-slate-700">
                {t("lb_embed_onboarding_headline")}
              </h2>

              <p className="mb-8 leading-relaxed text-slate-700">{t("lb_embed_onboarding_subtitle")}</p>

              <ul className="flex flex-col gap-y-5 text-lg">
                <li className="flex items-center">
                  <img
                    src="https://cdn.prod.website-files.com/62bc01b9271a07a5da138777/63bd346312055b2cbed676d9_li_verified.svg"
                    alt="Icon placeholder"
                    className="mr-2 h-7 w-7 shrink-0 object-contain"
                  />
                  <span className="font-bold leading-snug text-slate-700">
                    {t("lb_embed_onboarding_benefit_1")}
                  </span>
                </li>

                <li className="flex items-center">
                  <img
                    src="https://cdn.prod.website-files.com/62bc01b9271a07a5da138777/63bd346312055b2cbed676d9_li_verified.svg"
                    alt="Icon placeholder"
                    className="mr-2 h-7 w-7 shrink-0 object-contain"
                  />
                  <span className="font-bold leading-snug text-slate-700">
                    {t("lb_embed_onboarding_benefit_2")}
                  </span>
                </li>

                <li className="flex items-center">
                  <img
                    src="https://cdn.prod.website-files.com/62bc01b9271a07a5da138777/63bd346312055b2cbed676d9_li_verified.svg"
                    alt="Icon placeholder"
                    className="mr-2 h-7 w-7 shrink-0 object-contain"
                  />
                  <span className="font-bold leading-snug text-slate-700">
                    {t("lb_embed_onboarding_benefit_3")}
                  </span>
                </li>
              </ul>
            </div>
          </div>

          <div className="min-w-0 rounded-3xl border bg-white transition-shadow duration-300 hover:shadow-lg">
            <div className="mx-auto px-6 pb-10 pt-6">{children}</div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div>
        <Toaster position="bottom-right" containerClassName="mr-16 md:mr-20" />
      </div>

      <div className="flex min-h-screen flex-col md:flex-row">
        <div className="hidden md:flex md:w-2/5 md:min-w-96">
          <img
            src="https://cdn.prod.website-files.com/62bc01b9271a07a5da138777/67f2b48721d7196ce4d57b87_iStock-1324574648.avif"
            alt=""
            aria-hidden="true"
            className="h-full w-full object-cover"
          />
        </div>

        <div className="min-h-screen p-6 md:grow md:p-10">
          <div className="mx-auto max-w-lg">
            <Logo />
            <div className="h-5 md:mb-8" />
            {children}
          </div>
        </div>
      </div>
    </>
  );
}

function useIFrameSizeNotifier() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Only run when actually embedded
    if (window.parent === window) return;

    const el = ref.current ?? document.documentElement;

    const postHeight = () => {
      const height = el.getBoundingClientRect().height;
      window.parent.postMessage({ type: "iframe-resize", height }, "*");
    };

    const observer = new ResizeObserver(postHeight);
    observer.observe(el);

    postHeight(); // initial measurement

    return () => observer.disconnect();
  }, []);

  return ref;
}
